/**
 * AI Agent Workflow Page — Real-time visualization of the Azure AI Foundry
 * multi-agent orchestration system as deployed in Foundry-aiassessment-project.
 *
 * Reflects the exact agent topology, GPT-4.1 model deployments,
 * Azure AI Search knowledge retrieval, and text-embedding-3-large pipeline.
 *
 * Available across SME, EM, and Admin portals.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Card, CardContent, Chip, Alert,
  CircularProgress, Divider, Stack, Avatar, LinearProgress,
  Stepper, Step, StepLabel, StepContent, IconButton, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  SmartToy, Hub, Security, CloudQueue, Psychology, Code,
  Groups, Architecture, ExpandMore, PlayArrow,
  Timer, ArrowForward, FiberManualRecord, Refresh,
  Storage, Search, DataObject, Memory,
} from '@mui/icons-material';
import { authFetch } from '../../api/authFetch';

/* ══════════════════════════════════════════════════════════════
   Foundry Agent Definitions — exact names from Microsoft Foundry
   (Foundry-aiassessment-project, created 17/04/2026)
   ══════════════════════════════════════════════════════════════ */

interface FoundryAgent {
  /** Exact name as registered in Foundry */
  foundryName: string;
  /** Internal routing key used by the orchestrator */
  internalKey: string;
  /** Display name for UI */
  displayName: string;
  /** Foundry agent type */
  agentType: 'prompt';
  /** GPT-4.1 model deployment name */
  modelDeployment: string;
  /** Category this agent handles */
  category: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  /** Assessment domains handled */
  domains: string[];
  /** Whether the agent has a connected vector store (Azure AI Search knowledge) */
  hasVectorStore: boolean;
  /** Vector store ID if connected */
  vectorStoreId?: string;
}

const ORCHESTRATOR: FoundryAgent = {
  foundryName: 'ai-assessment-orchestratorengine',
  internalKey: 'manager',
  displayName: 'AI Assessment Orchestrator Engine',
  agentType: 'prompt',
  modelDeployment: 'orchestrator-gpt41',
  category: 'Orchestration',
  description: 'Central orchestrator that receives all assessment requests, resolves the target specialist agent via CATEGORY_DOMAIN_AGENT_MAPPING, and delegates using the delegate_to_agent tool. Never performs assessments directly.',
  icon: <Hub />,
  color: '#00338D',
  domains: [],
  hasVectorStore: false,
};

const SPECIALIST_AGENTS: FoundryAgent[] = [
  {
    foundryName: 'securitycomplianceagent',
    internalKey: 'cloudshield',
    displayName: 'Security Compliance Agent',
    agentType: 'prompt',
    modelDeployment: 'securitycompliance-gpt41',
    category: 'Security & Compliance',
    description: 'Evaluates cloud security posture, zero trust maturity, data protection controls, and compliance framework alignment using NIST, CIS, and ISO 27001 benchmarks.',
    icon: <Security />,
    color: '#D32F2F',
    domains: [
      'Cloud Security Adoption Assessment',
      'Zero Trust Maturity Assessment',
      'Data Security Copilot Assessment',
    ],
    hasVectorStore: true,
    vectorStoreId: 'vs_dOQVvUwCyZ9VpIqyfhzw8k1q',
  },
  {
    foundryName: 'ai-analytics-agent',
    internalKey: 'genaiops',
    displayName: 'AI Analytics Agent',
    agentType: 'prompt',
    modelDeployment: 'ai-analytics-gpt41',
    category: 'AI & Analytics',
    description: 'Assesses AI/ML readiness, generative AI maturity, data analytics capabilities, and Fabric analytics journey through structured evaluation of organizational AI adoption.',
    icon: <Psychology />,
    color: '#7B1FA2',
    domains: [
      'Azure AI Readiness Assessment',
      'AI Engineer Skills Assessment',
      'Agentic AI Readiness Assessment',
      'Fabric Analytics Journey Assessment',
      'GenAI Maturity Assessment',
    ],
    hasVectorStore: true,
    vectorStoreId: 'vs_dOQVvUwCyZ9VpIqyfhzw8k1q',
  },
  {
    foundryName: 'devopsagent',
    internalKey: 'devflow',
    displayName: 'DevOps Agent',
    agentType: 'prompt',
    modelDeployment: 'devops-gpt41',
    category: 'Development & Operations',
    description: 'Evaluates DevOps/DevSecOps capability maturity, CI/CD pipeline effectiveness, application modernization readiness, and Azure integration services adoption.',
    icon: <Code />,
    color: '#388E3C',
    domains: [
      'DevOps Capability Assessment',
      'App Data Modernization Assessment',
      'Azure Integration Service (AIS) Assessment',
      'DevSecOps Capability Assessment',
    ],
    hasVectorStore: true,
    vectorStoreId: 'vs_8KPjwVHN2e0QsbKTbvLPVG8E',
  },
  {
    foundryName: 'cloudstrategyagent',
    internalKey: 'adoptiq',
    displayName: 'Cloud Strategy Agent',
    agentType: 'prompt',
    modelDeployment: 'cloudstrategy-gpt41',
    category: 'Cloud Strategy & Adoption',
    description: 'Assesses cloud transformation maturity, Azure landing zone readiness, VMware migration paths, and alignment with Cloud Adoption Framework (CAF) and Well-Architected Framework (WAF).',
    icon: <CloudQueue />,
    color: '#1565C0',
    domains: [
      'Cloud Maturity Assessment',
      'Azure Landing Zone Assessment',
      'Azure VMware Solution Assessment',
      'WAF Assessment',
      'CAF Assessment',
    ],
    hasVectorStore: true,
    vectorStoreId: 'vs_gVo3FyTgfMZ4xMAvHLy4qwKP',
  },
  {
    foundryName: 'prodcollaborationagent',
    internalKey: 'poweradopt',
    displayName: 'Productivity & Collaboration Agent',
    agentType: 'prompt',
    modelDeployment: 'prodcollaboration-gpt41',
    category: 'Productivity & Collaboration',
    description: 'Evaluates Microsoft 365 and Copilot readiness, Power Platform adoption maturity, Dynamics 365 deployment readiness, and digital workplace transformation.',
    icon: <Groups />,
    color: '#E65100',
    domains: [
      'M365 Copilot Readiness Assessment',
      'Copilot Studio Readiness Assessment',
      'Power Platform Adoption Assessment',
      'Power Platform Well-Architected Assessment',
      'Dynamics 365 F&O Readiness Assessment',
      'Dynamics 365 Sales Customer & Field Service Discovery Assessment',
      'Dynamics 365 Contact Center Readiness Assessment',
    ],
    hasVectorStore: true,
    vectorStoreId: 'vs_5kK0Dnt89VdRkW4cnO4VzBZv',
  },
  {
    foundryName: 'baseline-architecture-agent',
    internalKey: 'blueprint',
    displayName: 'Baseline Architecture Agent',
    agentType: 'prompt',
    modelDeployment: 'baseline-architecture-gpt41',
    category: 'Baseline Architecture',
    description: 'Generates comprehensive Azure baseline architectures, solution designs aligned with Well-Architected Framework pillars, and landing zone blueprints.',
    icon: <Architecture />,
    color: '#00695C',
    domains: ['Architecture Generation'],
    hasVectorStore: true,
    vectorStoreId: 'vs_Kaclja03fJYjMIlBmTH95zOr',
  },
];

/* ── Recent assessment activity ── */
interface RecentAssessment {
  id: string;
  category: string;
  domain: string;
  status: string;
  created_at: string;
  organization_name?: string;
}

/* ══════════════════════════════════════════════════════════════
   Orchestration Workflow Steps — exact flow as implemented
   ══════════════════════════════════════════════════════════════ */
const WORKFLOW_STEPS = [
  {
    label: '1. Client Submits Assessment Request',
    description:
      'The client selects a category and domain from their assigned assessment domains, completes the domain-specific questionnaire, and submits via the Client Portal. The request payload includes category, domain, and all answers.',
  },
  {
    label: '2. Orchestrator Engine Receives Request',
    description:
      'The ai-assessment-orchestratorengine (orchestrator-gpt41) receives the request. It looks up CATEGORY_DOMAIN_AGENT_MAPPING to resolve which specialist agent handles the given category + domain combination.',
  },
  {
    label: '3. Knowledge Retrieval via Azure AI Search',
    description:
      'Before delegation, the system queries Azure AI Search indexes for relevant assessment frameworks, best practices, and benchmark data. Documents are vectorized using the text-embedding-3-large model and retrieved via hybrid search (vector + keyword).',
  },
  {
    label: '4. Agent Delegation via delegate_to_agent Tool',
    description:
      'The Orchestrator calls exactly ONE specialist agent using the delegate_to_agent(agent_key, task_description, assessment_data) tool. It passes the assessment answers plus retrieved knowledge context to the specialist.',
  },
  {
    label: '5. Specialist Agent Processes Assessment',
    description:
      'The specialist agent (e.g. securitycomplianceagent on securitycompliance-gpt41) evaluates each response against its domain expertise and retrieved knowledge. It scores items 0\u2013100 with risk levels: Critical (0\u201325), High (26\u201350), Medium (51\u201375), Low (76\u2013100).',
  },
  {
    label: '6. Vector Store Knowledge Augmentation (RAG)',
    description:
      'Agents with connected vector stores retrieve domain-specific documents during assessment. The text-embedding-3-large model converts queries into 3072-dimensional vectors for semantic similarity search against the knowledge base.',
  },
  {
    label: '7. Results Aggregation & Persistence',
    description:
      'The specialist returns scored results with per-item recommendations. The Orchestrator aggregates the response and persists to PostgreSQL with full audit trail (assessment_id, scores, rationale, risk levels, metadata).',
  },
  {
    label: '8. Post-Processing & Notification Pipeline',
    description:
      'Draft report generation (PPTX via python-pptx + Excel), in-app notification to SME/EM via AdminNotification table, email alerts via Azure Communication Services (ACS), and the Validation Queue is populated for SME review.',
  },
];

/* ══════════════════════════════════════════════════════════════
   Component
   ══════════════════════════════════════════════════════════════ */
const AIAgentWorkflow: React.FC = () => {
  const [recentAssessments, setRecentAssessments] = useState<RecentAssessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeAgent, setActiveAgent] = useState<string | null>(null);
  const animationRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchRecent = useCallback(async () => {
    try {
      const res = await authFetch('/api/assessments/list?limit=10');
      const data = await res.json();
      if (data.success) setRecentAssessments(data.assessments || []);
    } catch { /* best-effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  /* Cycle through agents to simulate live orchestration */
  useEffect(() => {
    const keys = [ORCHESTRATOR.internalKey, ...SPECIALIST_AGENTS.map((a) => a.internalKey)];
    let idx = 0;
    animationRef.current = setInterval(() => {
      setActiveAgent(keys[idx % keys.length]);
      idx++;
    }, 2800);
    return () => { if (animationRef.current) clearInterval(animationRef.current); };
  }, []);

  const agentByCategory = (category: string) =>
    SPECIALIST_AGENTS.find((a) => a.category === category);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
        AI Agent Workflow
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Real-time visualization of the Azure AI Foundry multi-agent orchestration system
        as deployed in <strong>Foundry-aiassessment-project</strong>.
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Chip label="GPT-4.1 Models" size="small" variant="outlined" />
        <Chip label="text-embedding-3-large" size="small" variant="outlined" />
        <Chip label="Azure AI Search" size="small" variant="outlined" />
        <Chip label="7 Prompt Agents" size="small" variant="outlined" />
      </Stack>

      {/* ════════════════════════════════════════════════════
          Section 1 — Infrastructure Layer
          ════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f0f4ff 0%, #e8edf5 100%)' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <DataObject sx={{ color: '#00338D' }} />
          <Typography variant="h6" fontWeight={600}>Foundry Infrastructure</Typography>
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
          {/* Azure AI Search */}
          <Card variant="outlined">
            <CardContent sx={{ pb: '12px !important' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Avatar sx={{ bgcolor: '#0078D4', width: 36, height: 36 }}><Search /></Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Azure AI Search</Typography>
                  <Typography variant="caption" color="text.secondary">Knowledge Retrieval</Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                Hybrid search (vector + keyword) across assessment framework indexes.
                Documents are chunked, embedded, and indexed for real-time RAG retrieval by specialist agents.
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Endpoint: <code>ai-onboarding-agent-search-dev</code>
              </Typography>
            </CardContent>
          </Card>

          {/* Embedding Model */}
          <Card variant="outlined">
            <CardContent sx={{ pb: '12px !important' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Avatar sx={{ bgcolor: '#6A1B9A', width: 36, height: 36 }}><Memory /></Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>text-embedding-3-large</Typography>
                  <Typography variant="caption" color="text.secondary">Vector Embedding Model</Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                Converts documents and queries into 3072-dimensional vectors for semantic similarity search.
                Powers the RAG pipeline that augments specialist agent reasoning.
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Dimensions: 3072 &bull; Max tokens: 8191
              </Typography>
            </CardContent>
          </Card>

          {/* Vector Stores */}
          <Card variant="outlined">
            <CardContent sx={{ pb: '12px !important' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Avatar sx={{ bgcolor: '#2E7D32', width: 36, height: 36 }}><Storage /></Avatar>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Vector Stores</Typography>
                  <Typography variant="caption" color="text.secondary">Agent Knowledge Bases</Typography>
                </Box>
              </Stack>
              <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
                {SPECIALIST_AGENTS.filter((a) => a.hasVectorStore).length} of {SPECIALIST_AGENTS.length} specialist
                agents have connected vector stores containing domain-specific assessment frameworks and best practices.
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {SPECIALIST_AGENTS.filter((a) => a.hasVectorStore).map((a) => (
                  <Chip key={a.internalKey} label={a.displayName.split(' ')[0]} size="small"
                    sx={{ fontSize: '0.65rem', height: 20, bgcolor: `${a.color}15`, color: a.color, fontWeight: 600 }} />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Paper>

      {/* ════════════════════════════════════════════════════
          Section 2 — Live Orchestration Pipeline
          ════════════════════════════════════════════════════ */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf5 100%)' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Hub sx={{ color: '#00338D' }} />
          <Typography variant="h6" fontWeight={600}>Agent Orchestration Pipeline</Typography>
          <Chip icon={<FiberManualRecord sx={{ fontSize: 10 }} />} label="Live" size="small"
            color="success" variant="outlined" sx={{ ml: 'auto' }} />
        </Stack>

        {/* Orchestrator Hub Card */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Card sx={{
            minWidth: 340,
            textAlign: 'center',
            border: activeAgent === 'manager' ? '2px solid #00338D' : '1px solid #ddd',
            boxShadow: activeAgent === 'manager' ? '0 0 20px rgba(0,51,141,0.3)' : 1,
            transition: 'all 0.5s ease',
          }}>
            <CardContent>
              <Avatar sx={{ bgcolor: '#00338D', width: 56, height: 56, mx: 'auto', mb: 1 }}>
                <Hub />
              </Avatar>
              <Typography variant="subtitle1" fontWeight={700}>
                {ORCHESTRATOR.foundryName}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Model: <strong>{ORCHESTRATOR.modelDeployment}</strong> &bull; Type: {ORCHESTRATOR.agentType}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                {ORCHESTRATOR.description}
              </Typography>
              {activeAgent === 'manager' && <LinearProgress sx={{ mt: 1, borderRadius: 2 }} />}
            </CardContent>
          </Card>
        </Box>

        {/* Delegation arrow */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Stack alignItems="center" spacing={0}>
            <ArrowForward sx={{ transform: 'rotate(90deg)', color: '#999', fontSize: 28 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              delegate_to_agent()
            </Typography>
          </Stack>
        </Box>

        {/* Specialist Agent Cards */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
          gap: 2,
        }}>
          {SPECIALIST_AGENTS.map((agent) => {
            const isActive = activeAgent === agent.internalKey;
            return (
              <Card key={agent.internalKey} sx={{
                border: isActive ? `2px solid ${agent.color}` : '1px solid #e0e0e0',
                boxShadow: isActive ? `0 0 16px ${agent.color}40` : 1,
                transition: 'all 0.5s ease',
              }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ bgcolor: agent.color, width: 40, height: 40 }}>
                      {agent.icon}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {agent.foundryName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {agent.modelDeployment}
                      </Typography>
                    </Box>
                    {isActive && (
                      <Chip label="Processing" size="small" color="warning" variant="outlined" />
                    )}
                  </Stack>

                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.73rem' }}>
                    {agent.description}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  {/* Vector store + domain count */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      {agent.domains.length} Domain{agent.domains.length > 1 ? 's' : ''}
                    </Typography>
                    <Chip
                      label={agent.hasVectorStore ? 'Vector Store \u2713' : 'No Vector Store'}
                      size="small"
                      variant="outlined"
                      color={agent.hasVectorStore ? 'success' : 'default'}
                      sx={{ fontSize: '0.6rem', height: 18 }}
                    />
                  </Stack>
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {agent.domains.slice(0, 3).map((d) => (
                      <Chip key={d} label={d} size="small" variant="outlined"
                        sx={{ fontSize: '0.63rem', height: 20 }} />
                    ))}
                    {agent.domains.length > 3 && (
                      <Chip label={`+${agent.domains.length - 3} more`} size="small"
                        sx={{ fontSize: '0.63rem', height: 20 }} />
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Paper>

      {/* ════════════════════════════════════════════════════
          Section 3 — RAG Pipeline Detail
          ════════════════════════════════════════════════════ */}
      <Accordion defaultExpanded sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Search sx={{ color: '#0078D4' }} />
            <Typography variant="h6" fontWeight={600}>
              Knowledge Retrieval Pipeline (RAG)
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Indexing Pipeline (Offline)
              </Typography>
              <Stepper orientation="vertical" activeStep={-1}>
                {[
                  { label: 'Document Upload', desc: 'Assessment frameworks, best practices, and benchmark data uploaded to Azure Blob Storage.' },
                  { label: 'Chunking & Preprocessing', desc: 'Documents split into semantic chunks with metadata (category, domain, source).' },
                  { label: 'Vector Embedding', desc: 'Each chunk embedded via text-embedding-3-large \u2192 3072-dim vector representation.' },
                  { label: 'Index Storage', desc: 'Vectors + metadata stored in Azure AI Search indexes with HNSW vector configuration.' },
                ].map((s, i) => (
                  <Step key={i} active expanded>
                    <StepLabel StepIconComponent={() => (
                      <Avatar sx={{ bgcolor: '#0078D4', width: 24, height: 24, fontSize: 12 }}>{i + 1}</Avatar>
                    )}>
                      <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Query Pipeline (Runtime)
              </Typography>
              <Stepper orientation="vertical" activeStep={-1}>
                {[
                  { label: 'Assessment Query', desc: 'Specialist agent formulates a query from the assessment domain + client answers.' },
                  { label: 'Query Embedding', desc: 'Query text embedded via text-embedding-3-large into 3072-dim vector.' },
                  { label: 'Hybrid Search', desc: 'Azure AI Search performs vector similarity + BM25 keyword search, returning top-K relevant chunks.' },
                  { label: 'Context Augmentation', desc: 'Retrieved documents injected into agent prompt as grounding context for assessment scoring.' },
                ].map((s, i) => (
                  <Step key={i} active expanded>
                    <StepLabel StepIconComponent={() => (
                      <Avatar sx={{ bgcolor: '#6A1B9A', width: 24, height: 24, fontSize: 12 }}>{i + 1}</Avatar>
                    )}>
                      <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                    </StepLabel>
                    <StepContent>
                      <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* ════════════════════════════════════════════════════
          Section 4 — End-to-End Workflow Steps
          ════════════════════════════════════════════════════ */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PlayArrow sx={{ color: '#00338D' }} />
            <Typography variant="h6" fontWeight={600}>End-to-End Assessment Workflow</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stepper orientation="vertical" activeStep={-1}>
            {WORKFLOW_STEPS.map((step, idx) => (
              <Step key={idx} active expanded>
                <StepLabel StepIconComponent={() => (
                  <Avatar sx={{ bgcolor: '#00338D', width: 28, height: 28, fontSize: 13 }}>{idx + 1}</Avatar>
                )}>
                  <Typography fontWeight={600}>{step.label}</Typography>
                </StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    {step.description}
                  </Typography>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </AccordionDetails>
      </Accordion>

      {/* ════════════════════════════════════════════════════
          Section 5 — Full Agent Roster Table
          ════════════════════════════════════════════════════ */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <SmartToy sx={{ color: '#483698' }} />
            <Typography variant="h6" fontWeight={600}>Foundry Agent Roster</Typography>
            <Chip label={`${SPECIALIST_AGENTS.length + 1} Agents`} size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Foundry Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Model Deployment</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Domains</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Vector Store</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Orchestrator row */}
              <TableRow sx={{ bgcolor: '#f0f4ff' }}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ bgcolor: ORCHESTRATOR.color, width: 26, height: 26 }}>{ORCHESTRATOR.icon}</Avatar>
                    <Typography variant="body2" fontWeight={700}>{ORCHESTRATOR.foundryName}</Typography>
                  </Stack>
                </TableCell>
                <TableCell><code style={{ fontSize: '0.75rem' }}>{ORCHESTRATOR.modelDeployment}</code></TableCell>
                <TableCell><Chip label={ORCHESTRATOR.agentType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /></TableCell>
                <TableCell>Orchestrator</TableCell>
                <TableCell align="center">&mdash;</TableCell>
                <TableCell align="center">&mdash;</TableCell>
              </TableRow>
              {/* Specialist rows */}
              {SPECIALIST_AGENTS.map((agent) => (
                <TableRow key={agent.internalKey} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ bgcolor: agent.color, width: 26, height: 26 }}>{agent.icon}</Avatar>
                      <Typography variant="body2" fontWeight={600}>{agent.foundryName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell><code style={{ fontSize: '0.75rem' }}>{agent.modelDeployment}</code></TableCell>
                  <TableCell><Chip label={agent.agentType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} /></TableCell>
                  <TableCell>{agent.category}</TableCell>
                  <TableCell align="center">
                    <Chip label={agent.domains.length} size="small" color="primary" />
                  </TableCell>
                  <TableCell align="center">
                    {agent.hasVectorStore ? (
                      <Tooltip title={agent.vectorStoreId || ''}>
                        <Chip label="Connected" size="small" color="success" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                      </Tooltip>
                    ) : (
                      <Chip label="None" size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* ════════════════════════════════════════════════════
          Section 6 — Recent Assessment Activity
          ════════════════════════════════════════════════════ */}
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Timer sx={{ color: '#C6007E' }} />
            <Typography variant="h6" fontWeight={600}>Recent Assessment Activity</Typography>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); fetchRecent(); }}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : recentAssessments.length === 0 ? (
            <Alert severity="info">No recent assessments. Activity will appear here once clients begin running assessments.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Domain</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Foundry Agent</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Model</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentAssessments.map((a) => {
                  const agent = agentByCategory(a.category);
                  return (
                    <TableRow key={a.id} hover>
                      <TableCell>{a.category}</TableCell>
                      <TableCell>{a.domain}</TableCell>
                      <TableCell>
                        {agent ? (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Avatar sx={{ bgcolor: agent.color, width: 22, height: 22, fontSize: 12 }}>{agent.icon}</Avatar>
                            <Typography variant="body2">{agent.foundryName}</Typography>
                          </Stack>
                        ) : '\u2014'}
                      </TableCell>
                      <TableCell>
                        {agent ? <code style={{ fontSize: '0.7rem' }}>{agent.modelDeployment}</code> : '\u2014'}
                      </TableCell>
                      <TableCell>
                        <Chip label={a.status} size="small"
                          color={a.status === 'completed' ? 'success' : a.status === 'pending' ? 'warning' : 'default'}
                          variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(a.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AIAgentWorkflow;
